import { faHeart } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { signIn } from "next-auth/react";
import { useEffect, useState } from "react";

type LikeIconButtonProps = {
  articleId: string;
  token?: string | null;
  /** Computed by the parent from the like list and `session.user._id`. */
  likedByMe?: boolean;
  likeCount?: number;
  onChanged: () => void;
  className?: string;
};

const LikeIconButton = ({
  articleId,
  token,
  likedByMe = false,
  likeCount = 0,
  onChanged,
  className = "",
}: LikeIconButtonProps) => {
  const [liked, setLiked] = useState(likedByMe);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Local state so the toggle can flip optimistically; the parent refetches the
  // like list after onChanged() and this re-syncs from the prop.
  useEffect(() => {
    setLiked(!!token && likedByMe);
  }, [token, likedByMe]);

  const handleToggleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!token) {
      signIn();
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const method = liked ? "DELETE" : "POST";
      const res = await fetch(`${process.env.BACKEND_BASE_URL}/contents/like/${articleId}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (data?.success === true) {
        setLiked(!liked);
        onChanged();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const likeCountText = Number.isFinite(likeCount)
    ? Math.max(0, likeCount).toLocaleString()
    : "0";

  return (
    <button
      type="button"
      onClick={handleToggleLike}
      aria-label={`${liked ? "Unlike article" : "Like article"} (${likeCountText})`}
      disabled={isSubmitting}
      // A 40px plate like the article's other actions; liked is signal blue.
      className={`flex h-10 min-w-[2.5rem] items-center justify-center gap-1.5 px-3 transition-colors duration-150 disabled:cursor-wait disabled:opacity-70 ${
        liked
          ? "bg-signal text-white hover:bg-[#0047CC]"
          : "bg-theme-surface text-theme-text hover:bg-signal hover:text-white"
      } ${className}`}
    >
      <FontAwesomeIcon icon={faHeart} className="text-sm" />
      <span className="font-display text-[13px] font-semibold leading-none tabular-nums">{likeCountText}</span>
    </button>
  );
};

export default LikeIconButton;
